import plur from 'plur'
import {useCallback, memo, useMemo} from 'react'

import EntityPath from '../../../containers/EntityPath'
import TransformText from '../../../containers/TransformText'
import {useIsAutodetectTimeZone} from '../../../hooks/useIsAutodetectTimeZone'
import useMyId from '../../../hooks/useMyId'
import type {Mute} from '../../../services/rest'
import {ResolutionType, stringifyId} from '../../../types'
import type {BuildTypeId, ProjectId} from '../../../types'
import {displayDate, parseDateToObj} from '../../../utils/dateTimeUtils'

import {getAssignmentName} from './Mutes.utils'

import styles from './Mutes.css'

type Props = {
  className?: string
  buildTypeId?: BuildTypeId | null | undefined
  currentlyMuted?: boolean | null | undefined
  mute: Mute
}

function MutesDetails({className, buildTypeId, currentlyMuted, mute}: Props) {
  const currentUserId = useMyId()
  const isAutodetectTimeZone = useIsAutodetectTimeZone()

  const {id, assignment, resolution, scope} = mute
  const {type: resolutionType, time: resolutionTime} = resolution ?? {}
  const assignmentName =
    stringifyId(id) !== '-1'
      ? getAssignmentName(mute, currentUserId === assignment?.user?.id)
      : 'successful retry'

  const scopeList = []
  const scopeProjectId = scope?.project?.id
  if (scopeProjectId != null) {
    scopeList.push({projectId: scopeProjectId})
  }

  const scopeBuildTypes = scope?.buildTypes?.buildType ?? []
  scopeBuildTypes.forEach(buildType => {
    scopeList.push({buildTypeId: buildType.id})
  })

  const timestampText = parseDateToObj(assignment?.timestamp ?? '', isAutodetectTimeZone).format(
    'D MMM YY LT',
  )

  const resolutionWay = useMemo(() => {
    switch (resolutionType) {
      case ResolutionType.MANUALLY:
        return 'manually'
      case ResolutionType.WHEN_FIXED:
        return 'automatically when fixed'
      case ResolutionType.AT_TIME: {
        return resolutionTime
          ? `automatically ${displayDate(resolutionTime, isAutodetectTimeZone, true)}`
          : null
      }
      default:
        return null
    }
  }, [isAutodetectTimeZone, resolutionTime, resolutionType])

  const renderEntityPath = useCallback(
    (data: {projectId?: ProjectId; buildTypeId?: BuildTypeId}) => {
      const key = `mute_${stringifyId(data.projectId) ?? stringifyId(data.buildTypeId)}`
      return (
        <EntityPath
          key={key}
          className={styles.entityPath}
          linkClassName={styles.entityPathLink}
          projectOrBuildTypeNode={{nodeType: 'all'}}
          withLeafStatusIcon={false}
          withCollapsing
          {...data}
        />
      )
    },
    [],
  )

  return (
    <div className={className}>
      <div className={styles.state}>
        {`${currentlyMuted ? `Currently muted` : `Muted in this build`} ${
          assignmentName ? `by ${assignmentName}` : ''
        }`}
        <span>{` ${timestampText}.`}</span>
        {resolutionWay != null && ` Unmute ${resolutionWay}`}
      </div>
      {scopeList.length > 0 && (
        <div className={styles.scope}>
          <span className={styles.scopeTitle}>{`${plur('Scope', scopeList.length)}:`}</span>
          <div className={styles.scopeList}>{scopeList.map(item => renderEntityPath(item))}</div>
        </div>
      )}
      {assignment?.text != null && assignment.text !== '' && (
        <div className={styles.comment}>
          <span>{'Comment: '}</span>
          <TransformText
            className={styles.commentText}
            loaderClassName={styles.commentTextLoader}
            buildTypeId={buildTypeId}
            text={assignment.text}
            showPlaceholder={false}
            lineHeight={20}
            TagName="span"
          />
        </div>
      )}
    </div>
  )
}

export default memo(MutesDetails)
